import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Alert } from "@/components/ui/alert";
import { DocActivity } from "@/modules/shell/DocActivity";
import { PageFrame } from "@/modules/shell/PageFrame";
import { PageHeader } from "@/modules/shell/PageHeader";
import { TagsAssigneesPanel } from "@/modules/tags/components/TagsAssigneesPanel";
import { getConversion } from "@/shared/api/conversions";
import {
  queryErrorMessage,
  useAuthRedirectOnQueryError,
} from "@/shared/query/query-error";
import { queryKeys } from "@/shared/query/keys";

export function ConversionDetailPage() {
  const { companySlug, conversionId } = useParams();

  const detailQuery = useQuery({
    queryKey: queryKeys.conversions.detail(
      companySlug ?? "",
      conversionId ?? "",
    ),
    enabled: Boolean(companySlug && conversionId),
    queryFn: () => getConversion(companySlug!, conversionId!),
  });
  const errorMsg = queryErrorMessage(detailQuery.error, detailQuery.isError);
  useAuthRedirectOnQueryError(errorMsg);

  if (!companySlug || !conversionId) return null;

  const displayError =
    errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;

  if (detailQuery.isPending || !detailQuery.data) {
    return (
      <PageFrame width="narrow" className="py-4">
        {displayError ? (
          <Alert variant="destructive">{displayError}</Alert>
        ) : (
          <Alert variant="muted">Loading conversion…</Alert>
        )}
      </PageFrame>
    );
  }

  const { conversion: cv } = detailQuery.data;

  return (
    <PageFrame width="narrow">
      <PageHeader
        eyebrow="Conversion"
        title={cv.number}
        description={[
          cv.lifecycle,
          cv.warehouseCode || cv.warehouseName,
        ]
          .filter(Boolean)
          .join(" · ")}
      />
      <TagsAssigneesPanel
        companySlug={companySlug}
        docType="stock_conversion"
        docId={conversionId}
        linkPath={`/c/${companySlug}/inventory/conversions/${conversionId}`}
      />

      <dl className="mt-6 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-[var(--color-muted)]">From</dt>
        <dd className="m-0">
          <Link
            to={`/c/${companySlug}/products/${cv.fromProductId}`}
            className="no-underline"
          >
            {cv.fromName}
          </Link>
          <span className="font-mono text-xs text-[var(--color-muted)]">
            {" "}
            · {cv.qtyFrom} @ {cv.unitCostFrom}
          </span>
        </dd>
        <dt className="text-[var(--color-muted)]">To</dt>
        <dd className="m-0">
          <Link
            to={`/c/${companySlug}/products/${cv.toProductId}`}
            className="no-underline"
          >
            {cv.toName}
          </Link>
          <span className="font-mono text-xs text-[var(--color-muted)]">
            {" "}
            · {cv.qtyTo} @ {cv.unitCostTo}
          </span>
        </dd>
        {(cv.warehouseName || cv.warehouseCode) && (
          <>
            <dt className="text-[var(--color-muted)]">Warehouse</dt>
            <dd className="m-0">
              {cv.warehouseName}
              {cv.warehouseCode ? (
                <span className="font-mono text-xs text-[var(--color-muted)]">
                  {" "}
                  · {cv.warehouseCode}
                </span>
              ) : null}
            </dd>
          </>
        )}
        {cv.postedAt && (
          <>
            <dt className="text-[var(--color-muted)]">Posted</dt>
            <dd className="m-0 text-xs">{cv.postedAt}</dd>
          </>
        )}
        {cv.notes && (
          <>
            <dt className="text-[var(--color-muted)]">Notes</dt>
            <dd className="m-0">{cv.notes}</dd>
          </>
        )}
      </dl>

      {displayError && (
        <Alert variant="destructive" className="mt-4">
          {displayError}
        </Alert>
      )}

      <DocActivity
        companySlug={companySlug}
        docType="stock_conversion"
        docId={conversionId}
        linkPath={`/c/${companySlug}/inventory/conversions/${conversionId}`}
      />
    </PageFrame>
  );
}
